import { createContext, useContext, useEffect, useState } from "react";
import { useConvex } from "convex/react";

import {
  markReinstallPurgeComplete,
  resolveAnonymousDeviceId,
  resetAnonymousDeviceId,
  setAnonymousDeviceId,
} from "../device-id";
import { mutationRef, queryRef } from "../convex/refs";
import { applyNativeRules } from "../native/rules";
import { syncNativeWebFilter } from "../native/webFilter";
import { useNotificationPermissionSessionSync } from "../native/useNotificationPermissionSessionSync";

type DeviceContextValue = {
  deviceId: string | null;
  ready: boolean;
  resetDeviceId: () => Promise<string>;
  /** One-time notice after post-reinstall rules/targets purge; cleared by the UI. */
  reinstallRulesNotice: boolean;
  dismissReinstallRulesNotice: () => void;
};

const DeviceContext = createContext<DeviceContextValue>({
  deviceId: null,
  ready: false,
  resetDeviceId: async () => {
    throw new Error("Device ID is not ready.");
  },
  reinstallRulesNotice: false,
  dismissReinstallRulesNotice: () => {},
});

type ResolveIdentityResult = {
  canonicalDeviceId: string;
  redirected: boolean;
};

type PurgeRulesAndTargetsResult = {
  rulesDeleted: number;
  targetsDeleted: number;
  rulePrefsReset: boolean;
};

const purgeRulesAndTargetsRef = mutationRef<
  { anonymousDeviceId: string },
  PurgeRulesAndTargetsResult
>("devices:purgeRulesAndTargetsForReinstall");

export function DeviceProvider({ children }: { children: React.ReactNode }) {
  const convex = useConvex();
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [reinstallRulesNotice, setReinstallRulesNotice] = useState(false);

  useNotificationPermissionSessionSync(deviceId);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const { deviceId: localId, reinstallDetected } = await resolveAnonymousDeviceId();
        const resolved = await convex.query(
          queryRef<{ anonymousDeviceId: string }, ResolveIdentityResult>("devices:resolveIdentity"),
          { anonymousDeviceId: localId },
        );
        let canonicalId = resolved.canonicalDeviceId.trim();

        if (reinstallDetected) {
          try {
            await convex.mutation(purgeRulesAndTargetsRef, { anonymousDeviceId: canonicalId });
            await applyNativeRules([], []);
            await syncNativeWebFilter([], []);
            await markReinstallPurgeComplete(canonicalId);
            if (!cancelled) setReinstallRulesNotice(true);
          } catch (error) {
            console.error("Post-reinstall rules/targets purge failed", error);
          }
        }

        if (canonicalId !== localId) {
          await setAnonymousDeviceId(canonicalId);
        }
        if (!cancelled) {
          setDeviceId(canonicalId);
          setReady(true);
        }
      } catch (error) {
        console.error("Failed to initialize device ID", error);
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [convex]);

  if (!ready) {
    return (
      <main className="screen0-shell grid content-center">
        <section className="mx-auto grid w-full max-w-[430px] gap-3">
          <h1 className="text-5xl font-semibold tracking-normal">Screen0</h1>
          <p className="text-lg text-[oklch(74%_0.025_155)]">Loading…</p>
        </section>
      </main>
    );
  }

  if (!deviceId) {
    return (
      <main className="screen0-shell grid content-center">
        <section className="mx-auto grid w-full max-w-[430px] gap-3">
          <h1 className="text-5xl font-semibold tracking-normal">Screen0</h1>
          <p className="text-lg text-[oklch(74%_0.025_155)]">Unable to initialize this device. Restart the app and try again.</p>
        </section>
      </main>
    );
  }

  async function resetDeviceId() {
    const id = await resetAnonymousDeviceId();
    setDeviceId(id);
    return id;
  }

  return (
    <DeviceContext.Provider
      value={{
        deviceId,
        ready,
        resetDeviceId,
        reinstallRulesNotice,
        dismissReinstallRulesNotice: () => setReinstallRulesNotice(false),
      }}
    >
      {children}
    </DeviceContext.Provider>
  );
}

export function useDeviceId() {
  const { deviceId } = useContext(DeviceContext);
  if (!deviceId) throw new Error("Device ID is not ready.");
  return deviceId;
}

export function useDeviceReady() {
  return useContext(DeviceContext);
}

export function useResetDeviceId() {
  return useContext(DeviceContext).resetDeviceId;
}
